import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/shadcn/lib/utils";
import { Spinner } from "../Spinner/Spinner";

type IconComponent = React.ComponentType<{
  className?: string;
  fill?: string;
  stroke?: string;
}>;

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "cursor-pointer rounded-md type-body-sm font-medium select-none",
    "transition-colors",
    "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
    "disabled:pointer-events-none disabled:cursor-default disabled:opacity-50 aria-disabled:cursor-default",
  ],
  {
    variants: {
      variant: {
        primary: ["bg-primary text-white", "hover:bg-primary/90", "focus-visible:ring-primary"],

        secondary: [
          "border border-border",
          "bg-background text-foreground",
          "focus-visible:ring-primary",
          "hover:bg-muted",
        ],

        ghost: ["text-foreground", "hover:bg-muted", "border border-border"],

        destructive: [
          "bg-destructive text-white",
          "hover:bg-destructive/90",
          "focus-visible:ring-destructive",
        ],

        success: ["bg-success text-white", "hover:bg-success/90", "focus-visible:ring-success"],

        destructiveOutline: [
          "border border-destructive",
          "bg-background text-destructive",
          "hover:bg-destructive/10",
          "focus-visible:ring-destructive",
        ],

        icon: ["text-muted-foreground", "hover:bg-muted"],

        ai: [
          "rounded-full bg-accent-purple-text text-white",
          "hover:bg-accent-purple-text/90",
          "focus-visible:ring-accent-purple-border",
        ],

        aiPrimary: [
          "bg-accent-purple-text text-white",
          "hover:bg-accent-purple-text/90",
          "focus-visible:ring-accent-purple-border",
        ],

        aiSecondary: [
          "border border-border bg-background text-accent-purple-text",
          "hover:bg-accent-purple-bg",
          "focus-visible:ring-accent-purple-border",
        ],

        aiOutline: [
          "shrink-0 rounded-full border border-accent-purple-border bg-background whitespace-nowrap text-accent-purple-text",
          "hover:bg-accent-purple-bg",
          "focus-visible:ring-accent-purple-border",
        ],
      },

      size: {
        sm: "h-8 px-3 type-body-xs",
        md: "h-10 px-4",
        lg: "h-11 px-6",
        icon: "h-10 w-10 p-0",
        iconSm: "h-8 w-8 p-0",
      },
    },

    defaultVariants: {
      variant: "secondary",
      size: "md",
    },
  },
);

export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> &
  VariantProps<typeof buttonVariants> & {
    readonly asChild?: boolean;
    readonly StartIcon?: IconComponent;
    readonly EndIcon?: IconComponent;
    readonly loading?: boolean;
    readonly fullWidth?: boolean;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      StartIcon,
      EndIcon,
      loading = false,
      fullWidth,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const iconClassName = size === "lg" ? "h-5 w-5" : "h-4 w-4";

    if (asChild) {
      return (
        <Slot
          aria-busy={loading || undefined}
          aria-disabled={isDisabled || undefined}
          className={cn(
            buttonVariants({ variant, size, className }),
            fullWidth && "w-full",
            isDisabled && "pointer-events-none opacity-50",
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        className={cn(buttonVariants({ variant, size, className }), fullWidth && "w-full")}
        disabled={isDisabled}
        ref={ref}
        type="button"
        {...props}
      >
        {loading ? (
          <Spinner
            aria-label="Loading"
            className={cn(
              "fill-current text-inherit [&>path:first-of-type]:opacity-25",
              iconClassName,
            )}
          />
        ) : (
          StartIcon && (
            <StartIcon aria-hidden="true" className={cn(iconClassName, "text-inherit")} />
          )
        )}

        {children}

        {!loading && EndIcon && <EndIcon aria-hidden="true" className={iconClassName} />}
      </button>
    );
  },
);

Button.displayName = "Button";

export { buttonVariants };
